package com.dignicate.web.viewmodels

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel

/**
 * Base ViewModel class for Compose HTML.
 *
 * Unlike Android's ViewModel, this doesn't survive configuration changes
 * (because web doesn't have that concept). It simply provides:
 * - viewModelScope: A CoroutineScope tied to this ViewModel's lifecycle
 * - Automatic cleanup when the Composable is disposed
 *
 * Lifecycle:
 * 1. Constructor runs
 * 2. init blocks run (for setup like binding)
 * 3. onCreated() is called from UI (after remember, for data fetching)
 * 4. onCleared() is called when the Composable is disposed (before scope cancellation)
 * 5. viewModelScope is automatically cancelled
 */
abstract class ViewModel {
    /**
     * CoroutineScope tied to this ViewModel.
     * This scope will be cancelled automatically when onDispose() is called.
     */
    protected val viewModelScope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    /**
     * Called when the ViewModel is created, after bindings are set up.
     * This should be called explicitly from the UI layer (e.g., in DisposableEffect or LaunchedEffect).
     * Override this method to handle initialization logic such as:
     * - Starting data fetching
     * - Triggering initial loads
     */
    open fun onCreated() {
        // Override in subclasses if needed
    }

    /**
     * Called when the ViewModel is being disposed.
     * Should be called from UI lifecycle (e.g., DisposableEffect).
     * This method calls onCleared() then cancels the viewModelScope.
     */
    fun onDispose() {
        onCleared()
        viewModelScope.cancel()
    }

    /**
     * Override this method to handle cleanup logic before the ViewModel is destroyed.
     * Called automatically by onDispose() before the viewModelScope is cancelled.
     *
     * This is similar to Android's ViewModel.onCleared().
     */
    protected open fun onCleared() {
        // Override in subclasses if needed
    }
}
